import { useState } from 'react'

/* The QC decision model, made explorable.
 *
 * The claim on this page is that quality control can ask a question instead of
 * sending a rejection. That claim only lands once a reader sees that "needs
 * clarification" is a FIFTH outcome sitting between pass and fail — which is
 * the one thing a paragraph struggles to convey, because every reader arrives
 * assuming QC is binary.
 *
 * So the interaction is the argument: five verdicts, and picking one shows
 * what actually happens next. The clarification verdict is the only one whose
 * next step points backwards, and the loop arrow in the diagram is drawn from
 * it alone.
 *
 * Defaults to that verdict rather than to "pass". A reader who never clicks
 * still leaves having read the differentiator.
 */

interface Verdict {
  key: string
  label: string
  tone: 'good' | 'warn' | 'ask' | 'human' | 'bad'
  headline: string
  body: string
  next: string
}

const VERDICTS: Verdict[] = [
  {
    key: 'pass',
    label: 'Pass',
    tone: 'good',
    headline: 'Approved automatically',
    body:
      'Every rule cleared. The release is approved without a person opening it, and the full ' +
      'evidence — what was checked, what was found — is written to the audit trail so the ' +
      'decision can be defended months later.',
    next: 'Goes straight to delivery.',
  },
  {
    key: 'warn',
    label: 'Pass with warnings',
    tone: 'warn',
    headline: 'Approved, with notes attached',
    body:
      'Nothing blocking, but something worth knowing — a convention a store prefers, a choice ' +
      'that will limit where the release can be pitched. The release proceeds and the notes ' +
      'travel with it.',
    next: 'Delivered. Notes visible to the label and the artist.',
  },
  {
    key: 'clarify',
    label: 'Needs clarification',
    tone: 'ask',
    headline: 'The uploader is asked, mid-upload',
    body:
      'Something is ambiguous rather than wrong: a featured artist in the title but not in the ' +
      'credits, a version tag that might be a remix, a language that does not match the lyrics. ' +
      'Instead of rejecting it, the run asks — in plain language, in the upload session, with ' +
      'the correction already written out and one click away.',
    next: 'Answer accepted → only the affected checks re-run. Unanswered → held, never failed.',
  },
  {
    key: 'human',
    label: 'Manual review',
    tone: 'human',
    headline: 'Routed to a person, by reason',
    body:
      'Some findings should never be decided by software: a likeness that may not belong to the ' +
      'uploader, a sample match, an impersonation signal. These go to a human queue tagged with ' +
      'why they are there, rather than into one undifferentiated pile.',
    next: 'Reviewed by a person, with the evidence already assembled.',
  },
  {
    key: 'fail',
    label: 'Fail',
    tone: 'bad',
    headline: 'Refused, with the reason named',
    body:
      'A rule that stores enforce has been broken and cannot be patched by an answer. The ' +
      'refusal names the rule and what would satisfy it — the point is that the next attempt ' +
      'succeeds, not that this one was rejected.',
    next: 'Returned to the uploader with the specific fix required.',
  },
]

const TONES: Record<Verdict['tone'], { chip: string; active: string; accent: string }> = {
  good: {
    chip: 'border-emerald-400/25 text-emerald-300/70',
    active: 'border-emerald-400/50 bg-emerald-400/10 text-emerald-300',
    accent: 'text-emerald-300',
  },
  warn: {
    chip: 'border-amber-400/25 text-amber-300/70',
    active: 'border-amber-400/50 bg-amber-400/10 text-amber-300',
    accent: 'text-amber-300',
  },
  ask: {
    chip: 'border-[#ff6a1a]/30 text-[#ff8c4d]/75',
    active: 'border-[#ff6a1a]/60 bg-[#ff6a1a]/12 text-[#ff8c4d]',
    accent: 'text-[#ff8c4d]',
  },
  human: {
    chip: 'border-sky-400/25 text-sky-300/70',
    active: 'border-sky-400/50 bg-sky-400/10 text-sky-300',
    accent: 'text-sky-300',
  },
  bad: {
    chip: 'border-rose-400/25 text-rose-300/70',
    active: 'border-rose-400/50 bg-rose-400/10 text-rose-300',
    accent: 'text-rose-300',
  },
}

const CHECKS = ['Metadata', 'Artwork', 'Audio', 'Content match']

export default function QcVerdictFlow() {
  const [active, setActive] = useState<Verdict>(
    VERDICTS.find((v) => v.key === 'clarify') ?? VERDICTS[0]
  )
  const tone = TONES[active.tone]

  return (
    <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 sm:p-9">
      {/* The four check lanes converging on one decision. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
        <div className="grid flex-1 grid-cols-2 gap-2">
          {CHECKS.map((check) => (
            <span
              key={check}
              className="rounded-lg border border-[#ff6a1a]/25 bg-[#ff6a1a]/[0.07] px-3 py-2 text-center text-[0.75rem] text-white/70"
            >
              {check}
            </span>
          ))}
        </div>

        <svg
          className="hidden h-16 w-10 shrink-0 sm:block"
          viewBox="0 0 40 64"
          aria-hidden="true"
          fill="none"
        >
          {[10, 26, 38, 54].map((y) => (
            <path
              key={y}
              d={`M 0 ${y} C 18 ${y} 18 32 34 32`}
              stroke="rgba(255,106,26,0.3)"
              strokeWidth="1.5"
            />
          ))}
          <circle cx="36" cy="32" r="3" fill="#ff6a1a" />
        </svg>

        <div className="shrink-0 rounded-xl border border-[#ff6a1a]/25 bg-[#ff6a1a]/[0.07] px-4 py-3 text-center">
          <p className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-[#ff8c4d]">
            One verdict
          </p>
          <p className="mt-1 text-[0.8125rem] font-semibold text-white">Five outcomes</p>
        </div>
      </div>

      {/* Pick a verdict. */}
      <div
        role="group"
        aria-label="Choose a quality-control verdict"
        className="mt-7 flex flex-wrap gap-2"
      >
        {VERDICTS.map((verdict) => {
          const isActive = verdict.key === active.key
          const style = TONES[verdict.tone]
          return (
            <button
              key={verdict.key}
              type="button"
              aria-pressed={isActive}
              onClick={() => setActive(verdict)}
              className={`rounded-full border px-3.5 py-1.5 text-[0.8125rem] font-medium transition-all ${
                isActive ? style.active : `${style.chip} hover:bg-white/[0.03]`
              }`}
            >
              {verdict.label}
            </button>
          )
        })}
      </div>

      {/* What happens next. aria-live so a screen reader hears the change
          rather than only sighted users seeing it. */}
      <div
        aria-live="polite"
        className="mt-6 rounded-2xl border border-white/[0.07] bg-black/25 p-6"
      >
        <p className={`text-[0.9375rem] font-semibold ${tone.accent}`}>{active.headline}</p>
        <p className="mt-3 text-[0.875rem] leading-[1.8] text-white/60">{active.body}</p>
        <p className="mt-5 flex items-start gap-2.5 border-t border-white/[0.07] pt-4 text-[0.8125rem] leading-relaxed text-white/45">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-white/30" aria-hidden="true" />
          {active.next}
        </p>
      </div>

      <p className="mt-4 text-center text-[0.75rem] text-white/45">
        Four of these outcomes exist in every distributor&rsquo;s quality control. The third one is
        the reason artists stop resenting it.
      </p>
    </div>
  )
}
